import { useImperativeHandle, useRef } from "react";

import { type BaseObject } from "../../../../evidence/base";
import { useDataSource } from "../data-source";
import { useDataTableState } from "../data-table-state";
import { useReactTable } from "../react-table/react-table";
import { type UseDataTableParams, type UseDataTableResult } from "./types";

export function useDataTable<TTableData extends BaseObject, TValue>({
  id,
  version,
  queryKey,
  api,
  apiSuffix,
  schema,
  fetchData,
  ref,
  columns,
  defaultSorting,
  defaultVisibility,
  defaultPagination,
  defaultGlobalFilter,
  defaultColumnFilters,
  preFilters,
}: UseDataTableParams<TTableData, TValue>): UseDataTableResult<TTableData> {
  const tableBodyRef = useRef<HTMLTableSectionElement>(null);

  const { dndContext, initialState, state, stateHandlers, applyProfile } =
    useDataTableState({
      defaultGlobalFilter,
      defaultColumnFilters,
      defaultPagination,
      defaultSorting,
      defaultVisibility,
      defaultColumnOrder: columns
        .map((column) => column.id!)
        .filter((id) => id !== "select" && id !== "open"),
      id,
      version,
    });

  const { dataQuery, defaultData, pageCount, fetchDataOptions } = useDataSource(
    {
      id,
      queryKey,
      api,
      apiSuffix,
      schema,
      state,
      preFilters,
      fetchData,
      columns,
    },
  );

  const table = useReactTable<TTableData, TValue>({
    data: dataQuery.data?.items ?? defaultData,
    columns,
    pageCount,
    state: {
      initialState,
      state,
      stateHandlers,
    },
  });

  useImperativeHandle(ref, () => ({
    tableBodyRef,

    initialState,
    state,
    stateHandlers,

    dataQuery,
    fetchDataOptions,

    table,
  }));

  return {
    tableBodyRef,
    dndContext,
    dataQuery,
    fetchDataOptions,
    table,
    applyProfile,
  };
}
